/**
 * Native Auth System per Cloudflare D1
 * Compatibile con l'interfaccia Firebase ma usa API native
 */

interface AuthUser {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName?: string | null;
  photoURL?: string | null;
  role?: 'sponsor' | 'manager' | 'admin';
}

interface AuthCredential {
  user?: AuthUser;
  error?: string;
}

interface AuthResult {
  user: AuthUser | null;
  credential?: AuthCredential;
}

class NativeAuth {
  private currentUser: AuthUser | null = null;
  private listeners: Array<(user: AuthUser | null) => void> = [];

  constructor() {
    this.initializeFromStorage();
  }

  /**
   * Initialize auth state from localStorage
   */
  private initializeFromStorage() {
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken) {
      // Validazione token con backend
      this.fetchCurrentUser().catch(() => {
        this.clearSession();
      });
    }
  }

  /**
   * Fetch current user from backend
   */
  private async fetchCurrentUser(): Promise<AuthUser | null> {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) return null;

      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      if (!response.ok) {
        throw new Error('Invalid token');
      }

      const userData = await response.json();
      this.currentUser = {
        uid: userData.id,
        email: userData.email,
        emailVerified: true,
        displayName: userData.displayName,
        photoURL: userData.photoURL,
        role: userData.role
      };

      this.notifyListeners();
      return this.currentUser;
    } catch (error) {
      this.clearSession();
      return null;
    }
  }

  /**
   * Clear session data
   */
  private clearSession() {
    this.currentUser = null;
    localStorage.removeItem('session_token');
    this.notifyListeners();
  }

  /**
   * Notify all auth state listeners
   */
  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.currentUser));
  }

  /**
   * Sign up new user
   */
  async createUserWithEmailAndPassword(email: string, password: string): Promise<AuthResult> {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Registration failed');
      }

      const user: AuthUser = {
        uid: data.user.id,
        email: data.user.email,
        emailVerified: false,
        displayName: data.user.displayName,
        role: data.user.role
      };

      if (data.token) {
        localStorage.setItem('session_token', data.token);
      }

      this.currentUser = user;
      this.notifyListeners();

      return { user };
    } catch (error) {
      return {
        user: null,
        credential: { error: error instanceof Error ? error.message : 'Registration failed' }
      };
    }
  }

  /**
   * Sign in existing user
   */
  async signInWithEmailAndPassword(email: string, password: string): Promise<AuthResult> {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Login failed');
      }

      const user: AuthUser = {
        uid: data.user.id,
        email: data.user.email,
        emailVerified: true,
        displayName: data.user.displayName,
        role: data.user.role
      };

      if (data.token) {
        localStorage.setItem('session_token', data.token);
      }

      this.currentUser = user;
      this.notifyListeners();

      return { user };
    } catch (error) {
      return {
        user: null,
        credential: { error: error instanceof Error ? error.message : 'Login failed' }
      };
    }
  }

  /**
   * Sign out current user
   */
  async signOut(): Promise<void> {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearSession();
    }
  }

  /**
   * Send password reset email
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    const response = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Password reset failed');
    }
  }

  /**
   * Get current user
   */
  get getCurrentUser(): AuthUser | null {
    return this.currentUser;
  }

  /**
   * Subscribe to auth state changes
   */
  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    this.listeners.push(callback);

    // Call immediately with current state
    callback(this.currentUser);

    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }
}

// Export singleton instance
export const auth = new NativeAuth();
export default auth;
